import { AlertCircle, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { CustomHeaderItem } from '../types/header';
import { getImageUrl } from '../utils';

interface UnselectDefaultModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (headerId: string) => Promise<void>;
    headerData: CustomHeaderItem | null;
}

const UnselectDefaultModal: React.FC<UnselectDefaultModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    headerData
}) => {
    // State
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setIsProcessing(false);
        }
    }, [isOpen]);

    // Handle confirmation
    const handleConfirm = async (): Promise<void> => {
        if (!headerData) return;

        setIsProcessing(true);
        try {
            await onConfirm(headerData._id);
            onClose();
        } catch (error) {
            console.error('Error unselecting default header:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    // If modal is closed or no header data is provided, don't render
    if (!isOpen || !headerData) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                {/* Modal header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-semibold">Remove Default Header</h2>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-gray-100"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Modal body */}
                <div className="p-4">
                    {/* Header preview */}
                    <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-2">Selected header:</p>
                        <div className="flex items-center p-2 bg-gray-50 rounded-md">
                            <div className="w-12 h-8 overflow-hidden rounded border mr-3">
                                {headerData.images && (
                                    <img
                                        src={getImageUrl(headerData.images)}
                                        alt={headerData.name}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </div>
                            <span className="font-medium">{headerData.name}</span>
                        </div>
                    </div>

                    {/* Warning message */}
                    <div className="mb-4 p-3 bg-red-50 rounded-md text-sm">
                        <div className="flex">
                            <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="mb-2">
                                    <strong>Warning:</strong> This will remove this header as the default for your site.
                                </p>
                                <p>
                                    Your site will not have a default header until you set another one.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal footer */}
                <div className="p-4 border-t flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700"
                        disabled={isProcessing}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
                        disabled={isProcessing}
                    >
                        {isProcessing ? 'Removing...' : 'Remove Default'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnselectDefaultModal;